import React, { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Calendar, 
  Clock, 
  Play, 
  CheckCircle, 
  Users, 
  Video,
  Phone,
  FileText,
  ThumbsUp,
  Plus,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { 
  useOnboardingSessions, 
  type OnboardingSession, 
  type OnboardingChecklistItem 
} from '@/hooks/useOnboardingSessions';
import { useItemContacts } from '@/hooks/useItemContacts';

interface OnboardingSessionManagerProps {
  itemId: string;
  itemName?: string;
  catalogId?: string;
}

export default function OnboardingSessionManager({ itemId, itemName, catalogId }: OnboardingSessionManagerProps) {
  const { t } = useTranslation();
  const { 
    sessions, 
    isLoading, 
    activeSession, 
    completedSession,
    createSession,
    updateSession,
    startSession,
    completeSession,
    approveAndFinalize,
    isCreating,
  } = useOnboardingSessions(itemId, catalogId);

  const { contacts } = useItemContacts(itemId);

  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [meetingType, setMeetingType] = useState<'video' | 'voice'>('video');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [currentChecklist, setCurrentChecklist] = useState<OnboardingChecklistItem[]>([]);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [currentSession, setCurrentSession] = useState<OnboardingSession | null>(null);

  const handleSchedule = async () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error('Selecione data e hora');
      return;
    }

    const scheduled_at = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    
    // Criar lista de participantes baseada nos contacts do item
    const attendees = contacts.map(c => ({
      contact_id: c.contact_id,
      name: `${c.contact?.first_name || ''} ${c.contact?.last_name || ''}`.trim(),
      attended: false,
    }));

    try {
      await createSession({ 
        scheduled_at, 
        attendees,
        meeting_url: meetingType === 'video' ? meetingUrl : null,
        channel: meetingType,
      });
      setShowScheduleDialog(false);
      setScheduledDate('');
      setScheduledTime('');
      setMeetingType('video');
      setMeetingUrl('');
    } catch (error) {
      console.error(error);
    }
  };

  // Pegar telefone do contato principal
  const getPrimaryContactPhone = () => {
    if (contacts.length === 0) return null;
    const primary = contacts.find(c => (c as any).is_primary);
    const contact = primary?.contact || contacts[0]?.contact;
    if (contact) {
      return contact.primary_phone || null;
    }
    return null;
  };

  const handleStartSession = async (session: OnboardingSession) => {
    setCurrentSession(session);
    setCurrentChecklist(session.checklist);
    setMeetingNotes(session.meeting_notes || '');
    setRecordingUrl(session.recording_url || '');
    setShowSessionDialog(true);
    
    if (session.status === 'scheduled') {
      await startSession(session.id);
    }
  };

  const handleChecklistChange = (index: number, completed: boolean) => {
    const updated = [...currentChecklist];
    updated[index] = { ...updated[index], completed };
    setCurrentChecklist(updated);
  };

  const handleSaveProgress = async () => {
    if (!currentSession) return;
    
    await updateSession({
      id: currentSession.id,
      checklist: currentChecklist,
      meeting_notes: meetingNotes,
      recording_url: recordingUrl || null,
    });
    toast.success('Progresso salvo');
  };

  const handleCompleteSession = async () => {
    if (!currentSession) return;
    
    const allCompleted = currentChecklist.length === 0 || currentChecklist.every(item => item.completed);
    if (!allCompleted) {
      toast.error('Complete todos os itens do checklist antes de finalizar');
      return;
    }

    try {
      await completeSession({
        sessionId: currentSession.id,
        checklist: currentChecklist,
        meeting_notes: meetingNotes,
      });
      setShowSessionDialog(false);
      setCurrentSession(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleApproveAndFinalize = async (sessionId: string) => {
    if (!confirm('Confirma que o cliente aprovou o onboarding? Isso criará o case automaticamente.')) {
      return;
    }

    try {
      await approveAndFinalize(sessionId);
    } catch (error) {
      console.error(error);
    }
  };

  // Status do onboarding
  const getStatus = () => {
    if (completedSession) return 'completed';
    if (activeSession?.status === 'in_progress') return 'in_progress';
    if (activeSession?.status === 'scheduled') return 'scheduled';
    return 'pending';
  };

  const status = getStatus();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Video className="h-5 w-5" />
            Onboarding Session
            {itemName && <span className="text-muted-foreground font-normal">• {itemName}</span>}
          </CardTitle>
          
          <div className="flex items-center gap-2">
            {status === 'pending' && (
              <Button size="sm" onClick={() => setShowScheduleDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Agendar
              </Button>
            )}
            
            <Badge 
              variant={
                status === 'completed' ? 'default' :
                status === 'in_progress' ? 'secondary' :
                status === 'scheduled' ? 'outline' : 'destructive'
              }
            >
              {status === 'completed' && 'Concluído'}
              {status === 'in_progress' && 'Em andamento'}
              {status === 'scheduled' && 'Agendado'}
              {status === 'pending' && 'Pendente'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Nenhuma sessão agendada</p>
            <p className="text-sm">Agende a sessão de onboarding com o cliente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div 
                key={session.id}
                className="p-4 border rounded-lg space-y-3"
              >
                {/* Session Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {session.status === 'completed' && session.client_approved ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : session.status === 'completed' ? (
                      <ThumbsUp className="h-5 w-5 text-amber-500" />
                    ) : session.status === 'in_progress' ? (
                      <Play className="h-5 w-5 text-blue-500" />
                    ) : (
                      <Clock className="h-5 w-5 text-muted-foreground" />
                    )}
                    
                    <div>
                      <div className="font-medium">
                        {session.scheduled_at 
                          ? safeFormat(new Date(session.scheduled_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                          : 'Data não definida'
                        }
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {session.attendees.length} participante(s)
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {session.status === 'scheduled' && (
                      <Button size="sm" onClick={() => handleStartSession(session)}>
                        <Play className="h-4 w-4 mr-1" />
                        Iniciar
                      </Button>
                    )}
                    
                    {session.status === 'in_progress' && (
                      <Button size="sm" onClick={() => handleStartSession(session)}>
                        <FileText className="h-4 w-4 mr-1" />
                        Continuar
                      </Button>
                    )}
                    
                    {session.status === 'completed' && !session.client_approved && (
                      <Button 
                        size="sm" 
                        variant="default"
                        onClick={() => handleApproveAndFinalize(session.id)}
                      >
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        Aprovar e Criar Case
                      </Button>
                    )}
                    
                    {session.client_approved && (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Aprovado
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Checklist Progress */}
                {session.checklist.length > 0 && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progresso do Checklist</span>
                      <span>
                        {session.checklist.filter(i => i.completed).length} / {session.checklist.length}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full mt-1 overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ 
                          width: `${(session.checklist.filter(i => i.completed).length / session.checklist.length) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agendar Sessão de Onboarding</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input 
                  type="date" 
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Horário</Label>
                <Input 
                  type="time" 
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>

            {/* Tipo de Reunião */}
            <div className="space-y-3">
              <Label>Tipo de Reunião</Label>
              <RadioGroup 
                value={meetingType} 
                onValueChange={(v) => setMeetingType(v as 'video' | 'voice')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="video" id="type-video" />
                  <Label htmlFor="type-video" className="flex items-center gap-2 cursor-pointer">
                    <Video className="h-4 w-4" />
                    Vídeo Chamada
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="voice" id="type-voice" />
                  <Label htmlFor="type-voice" className="flex items-center gap-2 cursor-pointer">
                    <Phone className="h-4 w-4" />
                    Ligação Telefônica
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* URL da reunião para vídeo */}
            {meetingType === 'video' && (
              <div className="space-y-2">
                <Label>URL da Reunião (Zoom, Google Meet, etc.)</Label>
                <Input
                  value={meetingUrl}
                  onChange={(e) => setMeetingUrl(e.target.value)}
                  placeholder="https://zoom.us/j/..."
                />
              </div>
            )}

            {/* Telefone do contato para ligação */}
            {meetingType === 'voice' && (
              <div className="space-y-2">
                <Label>Telefone do Contato</Label>
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {getPrimaryContactPhone() || 'Nenhum telefone cadastrado'}
                  </span>
                </div>
              </div>
            )}

            {contacts.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Participantes ({contacts.length})
                </Label>
                <div className="flex flex-wrap gap-2">
                  {contacts.map((c) => (
                    <Badge key={c.id} variant="outline">
                      {c.contact?.first_name} {c.contact?.last_name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSchedule} disabled={isCreating}>
              {isCreating ? 'Agendando...' : 'Agendar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Conduct Dialog */}
      <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {currentSession?.channel === 'voice' ? (
                <Phone className="h-5 w-5" />
              ) : (
                <Video className="h-5 w-5" />
              )}
              Sessão de Onboarding
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Informações da Reunião */}
            {currentSession && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {currentSession.channel === 'video' ? (
                    <>
                      <Video className="h-4 w-4 text-primary" />
                      <span className="font-medium">Vídeo Chamada</span>
                    </>
                  ) : (
                    <>
                      <Phone className="h-4 w-4 text-primary" />
                      <span className="font-medium">Ligação Telefônica</span>
                    </>
                  )}
                </div>
                {currentSession.channel === 'video' && currentSession.meeting_url && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => window.open(currentSession.meeting_url!, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir Reunião
                  </Button>
                )}
                {currentSession.channel === 'voice' && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{getPrimaryContactPhone() || 'Sem telefone'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Checklist */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Checklist</Label>
              {currentChecklist.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum item no checklist</p>
              ) : (
                <div className="space-y-2">
                  {currentChecklist.map((item, index) => (
                    <div 
                      key={item.key}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                    >
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={(checked) => 
                          handleChecklistChange(index, checked as boolean)
                        }
                      />
                      <span className={item.completed ? 'line-through text-muted-foreground' : ''}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recording URL */}
            <div className="space-y-2">
              <Label>Link da Gravação (opcional)</Label>
              <Input
                value={recordingUrl}
                onChange={(e) => setRecordingUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notas da Sessão</Label>
              <Textarea
                value={meetingNotes}
                onChange={(e) => setMeetingNotes(e.target.value)}
                placeholder="Registre pontos importantes da sessão..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSessionDialog(false)}>
              Fechar
            </Button>
            <Button variant="secondary" onClick={handleSaveProgress}>
              Salvar Progresso
            </Button>
            <Button onClick={handleCompleteSession}>
              <CheckCircle className="h-4 w-4 mr-1" />
              Finalizar Sessão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
